// Mangle effect blocks. Each effect is an async function from a source
// buffer (as PcmBuffer) + its params + the rendering AudioContext to a
// new PcmBuffer. Using PcmBuffer at the boundary keeps the chain-runner
// simple: no node teardown between blocks.
//
// Some effects are pure math (bitcrush, ringmod, distort, reverse).
// Others use OfflineAudioContext for quality (pitch, stretch, filter,
// convolve). The render function dispatches on the block kind.

import type { MangleBlock, Sample } from '../../types';
import type { SampleStorage } from '../../storage/idb';
import {
  fromPcm,
  reverse as reversePcm,
  toPcm,
  type PcmBuffer,
} from '../../pipeline';
import { applyConvolve } from './convolve';

export type MangleContext = {
  /** Real or OfflineAudioContext — used for filter / convolve / pitch /
   *  stretch which need node-graph rendering. */
  ctx: BaseAudioContext;
  /** Needed by convolve to load the IR sample. */
  storage: SampleStorage | null;
};

export async function applyBlock(
  pcm: PcmBuffer,
  block: MangleBlock,
  ctx: MangleContext,
): Promise<PcmBuffer> {
  switch (block.kind) {
    case 'reverse':
      return reversePcm(pcm);
    case 'bitcrush':
      return bitcrush(pcm, block);
    case 'ringmod':
      return ringmod(pcm, block);
    case 'distort':
      return distort(pcm, block);
    case 'pitch':
      return pitchShift(pcm, block);
    case 'stretch':
      return granularStretch(pcm, block);
    case 'filter':
      return biquadFilter(pcm, block);
    case 'convolve':
      return convolve(pcm, block, ctx);
  }
}

export async function runChain(
  pcm: PcmBuffer,
  chain: MangleBlock[],
  ctx: MangleContext,
): Promise<PcmBuffer> {
  let out = pcm;
  for (const b of chain) {
    out = await applyBlock(out, b, ctx);
  }
  return out;
}

// ── bitcrush: quantise to N bits + reduce effective sample rate.
function bitcrush(
  pcm: PcmBuffer,
  block: Extract<MangleBlock, { kind: 'bitcrush' }>,
): PcmBuffer {
  const bits = Math.max(1, Math.min(16, Math.round(block.bits)));
  const downsample = Math.max(1, Math.round(block.downsample));
  const step = Math.pow(2, bits - 1);
  const channels = pcm.channels.map((ch) => {
    const dst = new Float32Array(ch.length);
    let held = 0;
    for (let i = 0; i < ch.length; i++) {
      if (i % downsample === 0) {
        // Quantise the current sample.
        held = Math.round(ch[i]! * step) / step;
      }
      dst[i] = held;
    }
    return dst;
  });
  return { sampleRate: pcm.sampleRate, channels };
}

// ── ringmod: multiply against a sine carrier.
function ringmod(
  pcm: PcmBuffer,
  block: Extract<MangleBlock, { kind: 'ringmod' }>,
): PcmBuffer {
  const depth = clamp01(block.depth);
  const hz = Math.max(0, block.hz);
  const channels = pcm.channels.map((ch) => {
    const dst = new Float32Array(ch.length);
    const w = (2 * Math.PI * hz) / pcm.sampleRate;
    for (let i = 0; i < ch.length; i++) {
      const mod = Math.sin(i * w);
      const wet = ch[i]! * mod;
      dst[i] = ch[i]! * (1 - depth) + wet * depth;
    }
    return dst;
  });
  return { sampleRate: pcm.sampleRate, channels };
}

// ── distort: waveshaper via soft-clip family. `amount` 0..1 selects how
//    aggressive (0 = clean passthrough; 1 = heavy saturation).
function distort(
  pcm: PcmBuffer,
  block: Extract<MangleBlock, { kind: 'distort' }>,
): PcmBuffer {
  const amount = clamp01(block.amount);
  // Linearly interpolate between a true dry passthrough and a heavily-
  // saturated tanh. Done this way because tanh at drive=1 is NOT a
  // passthrough (tanh(x)/tanh(1) distorts small signals), and we want
  // `amount = 0` to mean "no change".
  const drive = 1 + amount * 30;
  const norm = Math.tanh(drive);
  const channels = pcm.channels.map((ch) => {
    const dst = new Float32Array(ch.length);
    for (let i = 0; i < ch.length; i++) {
      const v = ch[i]!;
      const wet = Math.tanh(v * drive) / norm;
      dst[i] = v * (1 - amount) + wet * amount;
    }
    return dst;
  });
  return { sampleRate: pcm.sampleRate, channels };
}

// ── pitch: resample via OfflineAudioContext. Shifts pitch AND duration.
//    "Independent" time-preserving pitch needs a phase-vocoder — deferred
//    to phase G1 (AudioWorklets). For now, semitones are useful for
//    octave jumps and chip-tune-style effects.
async function pitchShift(
  pcm: PcmBuffer,
  block: Extract<MangleBlock, { kind: 'pitch' }>,
): Promise<PcmBuffer> {
  const ratio = Math.pow(2, block.semitones / 12);
  const nCh = Math.max(1, pcm.channels.length);
  const inFrames = pcm.channels[0]?.length ?? 0;
  const outFrames = Math.max(1, Math.round(inFrames / ratio));
  const Ctor = getOfflineCtor();
  if (!Ctor) {
    // Node fallback — linear resample, same direction.
    return linearResample(pcm, outFrames);
  }
  const rCtx = new Ctor(nCh, outFrames, pcm.sampleRate);
  const buf = rCtx.createBuffer(nCh, inFrames, pcm.sampleRate);
  for (let c = 0; c < nCh; c++) {
    const copy = new Float32Array(pcm.channels[c]!.length);
    copy.set(pcm.channels[c]!);
    buf.copyToChannel(copy, c);
  }
  const src = rCtx.createBufferSource();
  src.buffer = buf;
  src.playbackRate.value = ratio;
  src.connect(rCtx.destination);
  src.start(0);
  const rendered = await rCtx.startRendering();
  return {
    sampleRate: pcm.sampleRate,
    channels: Array.from({ length: nCh }, (_, c) => {
      const copy = new Float32Array(rendered.length);
      copy.set(rendered.getChannelData(c));
      return copy;
    }),
  };
}

// ── granular stretch: overlap-add of short grains. Simple but usable.
//    factor > 1 = longer, factor < 1 = shorter. Pitch preserved because
//    each grain plays at native rate; we just vary the hop between them.
function granularStretch(
  pcm: PcmBuffer,
  block: Extract<MangleBlock, { kind: 'stretch' }>,
): PcmBuffer {
  const factor = Math.max(0.25, block.factor);
  const grainLen = Math.max(64, Math.round(block.grainMs * pcm.sampleRate / 1000));
  const overlap = clamp01(block.overlap);
  const hopIn = Math.max(1, Math.round(grainLen * (1 - overlap)));
  const hopOut = Math.max(1, Math.round(hopIn * factor));
  const inLen = pcm.channels[0]?.length ?? 0;
  const outLen = Math.max(1, Math.round(inLen * factor));
  const channels = pcm.channels.map((ch) => {
    const dst = new Float32Array(outLen);
    const env = hanning(grainLen);
    let readPos = 0;
    let writePos = 0;
    while (readPos < inLen) {
      for (let i = 0; i < grainLen; i++) {
        const r = readPos + i;
        const w = writePos + i;
        if (r >= inLen || w >= outLen) break;
        dst[w] = (dst[w] ?? 0) + ch[r]! * env[i]!;
      }
      readPos += hopIn;
      writePos += hopOut;
    }
    return dst;
  });
  return { sampleRate: pcm.sampleRate, channels };
}

function hanning(n: number): Float32Array {
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    out[i] = 0.5 * (1 - Math.cos((2 * Math.PI * i) / Math.max(1, n - 1)));
  }
  return out;
}

// ── filter: BiquadFilterNode in an OfflineAudioContext. The biquad
//    gives us proper IIR behaviour for LPF/HPF/BPF.
async function biquadFilter(
  pcm: PcmBuffer,
  block: Extract<MangleBlock, { kind: 'filter' }>,
): Promise<PcmBuffer> {
  const Ctor = getOfflineCtor();
  if (!Ctor) return pcm; // test fallback: passthrough
  const nCh = Math.max(1, pcm.channels.length);
  const length = pcm.channels[0]?.length ?? 0;
  if (length === 0) return pcm;
  const rCtx = new Ctor(nCh, length, pcm.sampleRate);
  const buf = rCtx.createBuffer(nCh, length, pcm.sampleRate);
  for (let c = 0; c < nCh; c++) {
    const copy = new Float32Array(pcm.channels[c]!.length);
    copy.set(pcm.channels[c]!);
    buf.copyToChannel(copy, c);
  }
  const src = rCtx.createBufferSource();
  src.buffer = buf;
  const filter = rCtx.createBiquadFilter();
  // Our type uses lpf/hpf/bpf shorthand; BiquadFilterNode wants the
  // spelled-out forms. Map here rather than widening the app type.
  filter.type =
    block.mode === 'lpf' ? 'lowpass' : block.mode === 'hpf' ? 'highpass' : 'bandpass';
  filter.frequency.value = block.hz;
  filter.Q.value = block.q;
  src.connect(filter);
  filter.connect(rCtx.destination);
  src.start(0);
  const rendered = await rCtx.startRendering();
  return {
    sampleRate: pcm.sampleRate,
    channels: Array.from({ length: nCh }, (_, c) => {
      const copy = new Float32Array(rendered.length);
      copy.set(rendered.getChannelData(c));
      return copy;
    }),
  };
}

// ── convolve with another locally-stored sample as IR. The DSP itself
// lives in convolve.ts (shared with the standalone Convolve verb); this
// shim only handles loading the IR from IDB.
async function convolve(
  pcm: PcmBuffer,
  block: Extract<MangleBlock, { kind: 'convolve' }>,
  mctx: MangleContext,
): Promise<PcmBuffer> {
  if (!mctx.storage) return pcm;
  const irSample = await mctx.storage.getSample(block.withSampleId);
  if (!irSample) return pcm;
  const irBlob = await mctx.storage.getBlob(irSample.storageKey);
  if (!irBlob) return pcm;
  const live = mctx.ctx;
  // decodeAudioData needs a live (not offline) context in most browsers.
  // We accept either — callers of runChain for commit pass the shared
  // AudioContext; for pure offline-only envs this degrades to passthrough.
  const decoder = (live as AudioContext).decodeAudioData
    ? (live as AudioContext)
    : null;
  if (!decoder) return pcm;
  const irBuffer = await decoder.decodeAudioData(await irBlob.arrayBuffer());
  const irPcm = toPcm(irBuffer);
  return applyConvolve(pcm, irPcm);
}

// ── helpers

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

type OfflineAudioContextCtor = new (
  channels: number,
  length: number,
  sampleRate: number,
) => OfflineAudioContext;

function getOfflineCtor(): OfflineAudioContextCtor | null {
  if (typeof OfflineAudioContext !== 'undefined') return OfflineAudioContext;
  const webkit = (globalThis as unknown as {
    webkitOfflineAudioContext?: OfflineAudioContextCtor;
  }).webkitOfflineAudioContext;
  return typeof webkit === 'function' ? webkit : null;
}

function linearResample(pcm: PcmBuffer, outFrames: number): PcmBuffer {
  const inFrames = pcm.channels[0]?.length ?? 0;
  const ratio = inFrames / Math.max(1, outFrames);
  return {
    sampleRate: pcm.sampleRate,
    channels: pcm.channels.map((ch) => {
      const dst = new Float32Array(outFrames);
      for (let i = 0; i < outFrames; i++) {
        const t = i * ratio;
        const i0 = Math.floor(t);
        const frac = t - i0;
        const a = ch[i0] ?? 0;
        const b = ch[i0 + 1] ?? a;
        dst[i] = a + (b - a) * frac;
      }
      return dst;
    }),
  };
}

// ── Presets: quick-start mangle chains.

export type PresetId = 'crystallize' | 'tape' | 'underwater' | 'bitreduce' | 'octave-down';

export function presetChain(id: PresetId): MangleBlock[] {
  switch (id) {
    case 'crystallize':
      return [
        { kind: 'pitch', semitones: 12 },
        { kind: 'filter', mode: 'hpf', hz: 600, q: 0.7 },
        { kind: 'distort', curve: 'soft', amount: 0.2 },
      ];
    case 'tape':
      return [
        { kind: 'filter', mode: 'lpf', hz: 4500, q: 0.5 },
        { kind: 'distort', curve: 'soft', amount: 0.35 },
      ];
    case 'underwater':
      return [
        { kind: 'filter', mode: 'lpf', hz: 600, q: 2 },
        { kind: 'ringmod', hz: 8, depth: 0.3 },
      ];
    case 'bitreduce':
      return [{ kind: 'bitcrush', bits: 6, downsample: 4 }];
    case 'octave-down':
      return [{ kind: 'pitch', semitones: -12 }];
  }
}

// Re-export so consumers only import one place.
export type { MangleBlock, Sample };
